import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  calculateFromAddedLeverage,
  calculateFromFundingAmount,
  calculateFromMerchantInstallment,
  daysToTermDisplay,
  formatOfferFactor,
  fundingAmountFor,
  merchantInstallmentFor,
  OFFER_FACTOR_STEP,
  roundOfferFactor,
  termDisplayDivisor,
  termDisplayToDays,
  termUnitLabel,
} from "@/utils/offerCalculations";
import { useOfferTermAlwaysDays } from "@/hooks/queries";
import { usePersistentOfferCalc, type LastEditedField } from "@/hooks/usePersistentOfferCalc";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/** Same copy as the Daily Balances “Possible Missed Payments” chip tip. */
const POSSIBLE_MISSED_TIP =
  "Shows how many payments the merchant would have missed if this deal was historical.";

interface OfferCalculatorProps {
  currentLeveragePct: number;
  averageRevenue: number;
  /** When true, renders as a compact inline row (no Paper/title) for use in dashboard top row */
  inline?: boolean;
  /** Borrower/application ID for persistent storage. When ID changes, calculator resets to defaults. */
  id?: number;
  /** @deprecated Kept for backwards compatibility — button has been removed */
  onSendToOfferDocs?: (values: {
    fundingAmount: number;
    factor: number;
    installment: number;
  }) => void;
  /** Fires whenever calculated offer values change (incl. read-only). Null when both funding and payment are 0. */
  onOfferChange?: (
    values: {
      fundingAmount: number;
      factor: number;
      installment: number;
      term: number;
      frequency: string;
      addedLeverage: number;
    } | null
  ) => void;
  /**
   * Hears which of funding, payment and added leverage was typed last (null: none yet): the
   * other two are worked out from it.
   */
  onLastEditedChange?: (field: LastEditedField) => void;
  /** Pre-fills the calculator fields (applied at mount via useState initializer). */
  initialValues?: {
    fundingAmount?: number;
    factor?: number;
    installment?: number;
    term?: number;
    frequency?: string;
    addedLeverage?: number;
  };
  /** When true, all fields are non-editable, rendered as greyed-out values. */
  readOnly?: boolean;
  /**
   * False: no localStorage draft (a chat card), and initialValues are the offer: the org's
   * defaults fill only the terms they leave out. Key the calculator to remount it for new values.
   */
  persistDraft?: boolean;
  /**
   * When > 0 and an offer payment is set, show a warning icon at the right end
   * of the Payment field (same count as the Daily Balances chip).
   */
  possibleMissedCount?: number;
  /** Scroll/navigate to Daily Balances when the missed-payments icon is clicked. */
  onPossibleMissedNavigate?: () => void;
  /**
   * Checks the terms against what an approve accepts (a chat card): a value outside them stays
   * as typed with a message under its field, and `onProblemsChange` hears the messages. Off (the
   * underwriting page), factor and term fall back as they always have.
   */
  checkTerms?: boolean;
  /** The messages under the fields, while `checkTerms`; empty once the terms are valid. */
  onProblemsChange?: (problems: string[]) => void;
}

/** An approve's limits (the backend's ApproveArgs): factor 1 to 999.9999 (7 digits, 4 of them
 * decimals), term 1 to 3650 days. */
const MIN_FACTOR = 1;
const MAX_FACTOR = 999.9999;
const MAX_TERM_DAYS = 3650;

const inputClass =
  "border-border bg-background text-xs font-semibold shadow-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none";

/** Comma-separate thousands; omit decimals when value is a whole number */
const formatMoney = (val: number): string => {
  const n = Number(val);
  if (!n || isNaN(n)) return "";
  const rounded = Math.round(n * 100) / 100;
  return rounded % 1 === 0
    ? rounded.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : rounded.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Show number without trailing .00 (e.g. 1.49 → "1.49", 17.00 → "17") */
const formatFactor = (val: number): string => {
  const n = Number(val);
  if (!n || isNaN(n)) return "";
  const rounded = Math.round(n * 100) / 100;
  return rounded % 1 === 0 ? String(rounded) : rounded.toFixed(2);
};

/** A field's message under it (checkTerms), in the house wording. */
function FieldProblem({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p role="alert" className="mt-1 text-[0.65rem] font-medium leading-tight text-destructive">
      {text}
    </p>
  );
}

const OfferCalculator: React.FC<OfferCalculatorProps> = ({
  currentLeveragePct,
  averageRevenue,
  inline = false,
  id,
  onOfferChange,
  onLastEditedChange,
  initialValues,
  readOnly = false,
  persistDraft = true,
  possibleMissedCount = 0,
  onPossibleMissedNavigate,
  checkTerms = false,
  onProblemsChange,
}) => {
  const [values, setValues, lastEdited, setLastEdited, offerDefaults] = usePersistentOfferCalc(
    id,
    initialValues,
    { persist: persistDraft }
  );
  const offerTermAlwaysDays = useOfferTermAlwaysDays();
  const termDisplay = daysToTermDisplay(
    values.term,
    values.frequency,
    offerDefaults.businessDaysPerMonth,
    offerTermAlwaysDays
  );
  const termLabel = offerTermAlwaysDays ? "Term" : `Term · ${termUnitLabel(values.frequency)}`;
  // Raw string state — held while focused so intermediate input (e.g. "17.") isn’t reformatted
  const [rawAddedLev, setRawAddedLev] = useState<string | null>(null);
  // checkTerms: what was typed, where the value alone would show something else ("" for a 0,
  // 1.495 for a cleared factor). Only those inputs change factor and term; a funding worked out
  // from the payment or leverage shows as worked out.
  const [rawFactor, setRawFactor] = useState<string | null>(null);
  const [rawTerm, setRawTerm] = useState<string | null>(null);
  const [rawFunding, setRawFunding] = useState<string | null>(null);

  const termMax = offerTermAlwaysDays
    ? MAX_TERM_DAYS
    : Math.floor(
        MAX_TERM_DAYS / termDisplayDivisor(values.frequency, offerDefaults.businessDaysPerMonth)
      );
  const termUnit = (offerTermAlwaysDays ? "Days" : termUnitLabel(values.frequency)).toLowerCase();
  // A term typed with a fraction would be sent cut to whole days ("45.5" as 45): said instead.
  const termFraction = rawTerm !== null && rawTerm.trim() !== "" && !Number.isInteger(+rawTerm);
  const problems = {
    factor:
      values.factor < MIN_FACTOR || values.factor > MAX_FACTOR
        ? `Factor must be between ${MIN_FACTOR} and ${MAX_FACTOR}`
        : null,
    term: termFraction
      ? `Term must be a whole number of ${termUnit}`
      : values.term < 1 || values.term > MAX_TERM_DAYS
        ? `Term must be between 1 and ${termMax} ${termUnit}`
        : null,
    funding: !(values.fundingAmount >= 0.01) ? "Funding must be at least $0.01" : null,
  };
  const problemList = checkTerms
    ? [problems.factor, problems.term, problems.funding].filter((p): p is string => p !== null)
    : [];
  const problemKey = problemList.join("\n");
  const onProblemsRef = useRef(onProblemsChange);
  useEffect(() => {
    onProblemsRef.current = onProblemsChange;
  }, [onProblemsChange]);
  useEffect(() => {
    onProblemsRef.current?.(problemKey ? problemKey.split("\n") : []);
  }, [problemKey]);

  const onOfferChangeRef = useRef(onOfferChange);
  useEffect(() => {
    onOfferChangeRef.current = onOfferChange;
  }, [onOfferChange]);

  const onLastEditedRef = useRef(onLastEditedChange);
  useEffect(() => {
    onLastEditedRef.current = onLastEditedChange;
  }, [onLastEditedChange]);
  useEffect(() => {
    onLastEditedRef.current?.(lastEdited);
  }, [lastEdited]);

  const showMissedPaymentsButton =
    possibleMissedCount > 0 &&
    values.installment > 0 &&
    values.term > 0 &&
    !!onPossibleMissedNavigate;

  // Emit live draft values (including read-only / approved) so balance coloring
  // can track payment/term/frequency without requiring save. Callers that gate
  // Approve should still require fundingAmount > 0.
  useEffect(() => {
    if (!onOfferChangeRef.current) return;
    if (!(values.fundingAmount > 0 || values.installment > 0)) {
      onOfferChangeRef.current(null);
    } else {
      onOfferChangeRef.current({
        fundingAmount: values.fundingAmount,
        factor: values.factor,
        installment: values.installment,
        term: values.term,
        frequency: values.frequency,
        addedLeverage: values.addedLeverage,
      });
    }
  }, [values]);

  // checkTerms: with no average revenue only the leverage can't be worked out (0); the payment
  // still follows the funding and the terms, as the server's does.
  const noRevenue = averageRevenue <= 0;
  const fromFunding = useCallback(
    (funding: number, factor: number, term: number, frequency: string) =>
      checkTerms && !(factor > 0 && term > 0)
        ? { addedLeverageInput: 0, merchantInstallment: 0 }
        : checkTerms && noRevenue
          ? {
              addedLeverageInput: 0,
              merchantInstallment: merchantInstallmentFor(
                funding,
                factor,
                term,
                frequency,
                offerDefaults.businessDaysPerMonth
              ),
            }
          : calculateFromFundingAmount(
              funding,
              averageRevenue,
              factor,
              term,
              frequency,
              offerDefaults.businessDaysPerMonth
            ),
    [checkTerms, noRevenue, averageRevenue, offerDefaults.businessDaysPerMonth]
  );
  // The same for a typed payment: the funding it pays back, the leverage 0 with no revenue.
  const fromInstallment = useCallback(
    (installment: number, factor: number, term: number, frequency: string) =>
      checkTerms && !(factor > 0 && term > 0)
        ? { addedLeverageInput: 0, fundingAmount: 0 }
        : checkTerms && noRevenue
          ? {
              addedLeverageInput: 0,
              fundingAmount: fundingAmountFor(
                installment,
                factor,
                term,
                frequency,
                offerDefaults.businessDaysPerMonth
              ),
            }
          : calculateFromMerchantInstallment(
              installment,
              averageRevenue,
              factor,
              term,
              frequency,
              offerDefaults.businessDaysPerMonth
            ),
    [checkTerms, noRevenue, averageRevenue, offerDefaults.businessDaysPerMonth]
  );

  const recalculateFromLastEdited = useCallback(() => {
    if (lastEdited === null) return;
    // With no revenue only the added leverage can't be worked out from.
    if (noRevenue && !(checkTerms && lastEdited !== "addedLeverage")) return;
    // checkTerms: the typed factor and term, never the org's default in place of a 0.
    const currentFactor = checkTerms ? values.factor : values.factor || offerDefaults.factor;
    const currentTerm = checkTerms ? values.term : values.term || offerDefaults.term;
    const currentFreq = values.frequency || offerDefaults.frequency;
    if (checkTerms && !(currentFactor > 0 && currentTerm > 0)) {
      // Nothing to work out from a 0: the worked-out figures are blank until it is fixed.
      const blank =
        lastEdited === "fundingAmount"
          ? { installment: 0, addedLeverage: 0 }
          : lastEdited === "installment"
            ? { fundingAmount: 0, addedLeverage: 0 }
            : { fundingAmount: 0, installment: 0 };
      setValues((prev) =>
        Object.entries(blank).every(([key, value]) => prev[key as keyof typeof blank] === value)
          ? prev
          : { ...prev, ...blank }
      );
      return;
    }
    if (lastEdited === "addedLeverage") {
      const { fundingAmount: fa, merchantInstallment: mi } = calculateFromAddedLeverage(
        values.addedLeverage,
        averageRevenue,
        currentFactor,
        currentTerm,
        currentFreq,
        offerDefaults.businessDaysPerMonth
      );
      setValues((prev) =>
        prev.fundingAmount === fa && prev.installment === mi
          ? prev
          : { ...prev, fundingAmount: fa, installment: mi }
      );
    } else if (lastEdited === "fundingAmount") {
      const { addedLeverageInput: al, merchantInstallment: mi } = fromFunding(
        values.fundingAmount,
        currentFactor,
        currentTerm,
        currentFreq
      );
      setValues((prev) =>
        prev.addedLeverage === al && prev.installment === mi
          ? prev
          : { ...prev, addedLeverage: al, installment: mi }
      );
    } else if (lastEdited === "installment") {
      const { addedLeverageInput: al, fundingAmount: fa } = fromInstallment(
        values.installment,
        currentFactor,
        currentTerm,
        currentFreq
      );
      setValues((prev) =>
        prev.addedLeverage === al && prev.fundingAmount === fa
          ? prev
          : { ...prev, addedLeverage: al, fundingAmount: fa }
      );
    }
  }, [
    lastEdited,
    values.addedLeverage,
    values.fundingAmount,
    values.installment,
    values.factor,
    values.term,
    values.frequency,
    averageRevenue,
    noRevenue,
    checkTerms,
    fromFunding,
    fromInstallment,
    offerDefaults,
    setValues,
  ]);

  useEffect(() => {
    recalculateFromLastEdited();
  }, [values.factor, values.term, values.frequency, recalculateFromLastEdited]);

  // checkTerms: a factor, term or frequency changed before any amount was typed works the payment
  // and the leverage out from the funding, so the terms shown multiply out.
  const editTerms = (patch: Partial<typeof values>) => {
    if (checkTerms && lastEdited === null) setLastEdited("fundingAmount");
    setValues((prev) => ({ ...prev, ...patch }));
  };

  const handleLeverageChange = (val: number) => {
    setLastEdited("addedLeverage");
    // The funding is worked out now: what was typed in it last no longer shows.
    setRawFunding(null);
    const { fundingAmount: fa, merchantInstallment: mi } = calculateFromAddedLeverage(
      val,
      averageRevenue,
      values.factor,
      values.term,
      values.frequency,
      offerDefaults.businessDaysPerMonth
    );
    setValues((prev) => ({ ...prev, addedLeverage: val, fundingAmount: fa, installment: mi }));
  };

  const handleFundingChange = (val: number) => {
    setLastEdited("fundingAmount");
    const { addedLeverageInput: al, merchantInstallment: mi } = fromFunding(
      val,
      values.factor,
      values.term,
      values.frequency
    );
    setValues((prev) => ({ ...prev, fundingAmount: val, addedLeverage: al, installment: mi }));
  };

  const handleInstallmentChange = (val: number) => {
    setLastEdited("installment");
    setRawFunding(null);
    const { addedLeverageInput: al, fundingAmount: fa } = fromInstallment(
      val,
      values.factor,
      values.term,
      values.frequency
    );
    setValues((prev) => ({ ...prev, installment: val, addedLeverage: al, fundingAmount: fa }));
  };

  // z-10 keeps the float label above field chrome (e.g. Payment’s absolute warn icon).
  const floatLabel =
    "absolute left-2 top-0 z-10 -translate-y-1/2 bg-card px-1 text-[0.58rem] font-semibold uppercase leading-none tracking-wide text-muted-foreground";

  // Columns follow the calculator's own width, not the viewport: the dashboard gives it
  // anywhere from ~12rem to 70rem depending on layout and sidebar. Three columns from
  // 16rem: at the narrowest widths larger amounts (from a Payment of ~12,345.67) clip,
  // which was judged better than wrapping the header sooner. Read-only values use the
  // inputs' padding so both fit the same widths.
  const calculatorContainer = "[container-type:inline-size]";
  const row = (
    <div className="grid w-full grid-cols-2 gap-x-2 gap-y-3 pt-2 [@container(min-width:16rem)]:grid-cols-3">
      {/* Row 1: Add Lev | Factor | Payment */}
      <div className="relative">
        <span className={floatLabel}>Add Lev</span>
        <div className="flex h-8 items-center overflow-hidden rounded-md border border-border bg-background">
          <span className="shrink-0 pl-1.5 text-[0.7rem] font-semibold text-foreground">
            {Math.round(currentLeveragePct)} +
          </span>
          <span className="mx-1 h-4 w-px shrink-0 bg-border" />
          {readOnly ? (
            <div className="flex h-8 flex-1 items-center rounded-md bg-muted/60 px-0.5 text-[0.7rem] font-semibold text-muted-foreground/90">
              {formatFactor(values.addedLeverage)}%
            </div>
          ) : (
            <>
              <Input
                size="sm"
                type="text"
                inputMode="decimal"
                className={cn(
                  inputClass,
                  "flex-1 border-0 px-0.5 shadow-none focus-visible:ring-0"
                )}
                value={rawAddedLev ?? formatFactor(values.addedLeverage)}
                onFocus={() => setRawAddedLev(formatFactor(values.addedLeverage))}
                onBlur={() => setRawAddedLev(null)}
                onChange={(e) => {
                  const filtered = e.target.value
                    .replace(/[^0-9.]/g, "")
                    .replace(/(\..*)\./g, "$1");
                  setRawAddedLev(filtered);
                  handleLeverageChange(Number(filtered) || 0);
                }}
              />
              <span className="shrink-0 pr-1.5 text-[0.7rem] text-muted-foreground">%</span>
            </>
          )}
        </div>
      </div>

      <div>
        <div className="relative rounded-md border border-border bg-background">
          <span className={floatLabel}>Factor</span>
          {readOnly ? (
            <div className="flex h-8 items-center rounded-md bg-muted/60 px-2 text-[0.7rem] font-semibold text-muted-foreground/90">
              {formatOfferFactor(values.factor)}
            </div>
          ) : (
            <Input
              size="sm"
              type="number"
              step={OFFER_FACTOR_STEP}
              inputMode="decimal"
              className={cn(inputClass, "border-0 shadow-none focus-visible:ring-0")}
              value={rawFactor ?? values.factor}
              aria-invalid={checkTerms && problems.factor ? true : undefined}
              // A valid factor shows as it is sent (1.23456 as 1.2346); a wrong one stays as typed.
              onBlur={() => {
                if (!problems.factor) setRawFactor(null);
              }}
              onChange={(e) => {
                if (!checkTerms) {
                  setValues((prev) => ({
                    ...prev,
                    factor: roundOfferFactor(Number(e.target.value) || offerDefaults.factor),
                  }));
                  return;
                }
                setRawFactor(e.target.value);
                editTerms({ factor: roundOfferFactor(Number(e.target.value) || 0) });
              }}
            />
          )}
        </div>
        {checkTerms && !readOnly && <FieldProblem text={problems.factor} />}
      </div>

      <div className="relative">
        <span className={floatLabel}>Payment</span>
        <div className="relative flex h-8 items-center overflow-hidden rounded-md border border-border bg-background">
          <span className="shrink-0 pl-1.5 text-[0.7rem] text-muted-foreground">$</span>
          {readOnly ? (
            <div
              className={cn(
                "flex h-8 min-w-0 flex-1 items-center bg-muted/60 px-1.5 text-[0.7rem] font-semibold text-muted-foreground/90",
                showMissedPaymentsButton && "pr-7"
              )}
            >
              {formatMoney(values.installment)}
            </div>
          ) : (
            <Input
              size="sm"
              type="text"
              inputMode="decimal"
              className={cn(
                inputClass,
                "min-w-0 flex-1 border-0 px-0.5 shadow-none focus-visible:ring-0",
                showMissedPaymentsButton && "pr-7"
              )}
              value={formatMoney(values.installment)}
              onChange={(e) =>
                handleInstallmentChange(Number(e.target.value.replace(/,/g, "")) || 0)
              }
            />
          )}
          {showMissedPaymentsButton && (
            <TooltipProvider delayDuration={0} skipDelayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    data-testid="possible-missed-payments"
                    onClick={() => onPossibleMissedNavigate?.()}
                    className="absolute right-1 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full bg-amber-100 text-amber-700 ring-1 ring-amber-200/80 transition-colors hover:bg-amber-200/80"
                    aria-label={`${possibleMissedCount} possible missed payments. Go to daily balances.`}
                  >
                    <AlertTriangle className="h-3 w-3" aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                  {possibleMissedCount} Possible Missed Payment
                  {possibleMissedCount !== 1 ? "s" : ""}. {POSSIBLE_MISSED_TIP}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {/* Row 2: Funding | Term | Freq */}
      <div className="relative">
        <span className={floatLabel}>Funding</span>
        <div className="flex h-8 items-center overflow-hidden rounded-md border border-border bg-background">
          <span className="shrink-0 pl-1.5 text-[0.7rem] text-muted-foreground">$</span>
          {readOnly ? (
            <div className="flex h-8 flex-1 items-center rounded-md bg-muted/60 px-0.5 text-[0.7rem] font-semibold text-muted-foreground/90">
              {formatMoney(values.fundingAmount)}
            </div>
          ) : (
            <Input
              size="sm"
              type="text"
              inputMode="decimal"
              className={cn(inputClass, "flex-1 border-0 px-0.5 shadow-none focus-visible:ring-0")}
              // As typed while it is wrong (0, 0.005): the value alone would show "" or "0.01".
              value={
                rawFunding !== null && problems.funding
                  ? rawFunding
                  : formatMoney(values.fundingAmount)
              }
              aria-invalid={checkTerms && problems.funding ? true : undefined}
              onChange={(e) => {
                const amount = Number(e.target.value.replace(/,/g, "")) || 0;
                // A 0 shows as typed (formatMoney blanks it), so its message has a value to explain.
                if (checkTerms) setRawFunding(e.target.value);
                handleFundingChange(amount);
              }}
            />
          )}
        </div>
        {checkTerms && !readOnly && <FieldProblem text={problems.funding} />}
      </div>

      <div>
        <div className="relative rounded-md border border-border bg-background">
          <span className={floatLabel}>{termLabel}</span>
          {readOnly ? (
            <div className="flex h-8 items-center rounded-md bg-muted/60 px-2 text-[0.7rem] font-semibold text-muted-foreground/90">
              {termDisplay || "-"}
            </div>
          ) : (
            <Input
              size="sm"
              type="number"
              className={cn(inputClass, "border-0 shadow-none focus-visible:ring-0")}
              value={rawTerm ?? (termDisplay || "")}
              aria-invalid={checkTerms && problems.term ? true : undefined}
              onBlur={() => {
                if (!problems.term) setRawTerm(null);
              }}
              onChange={(e) => {
                const val = e.target.value;
                if (checkTerms) {
                  // As typed: 0 and 99999 stay, with the range under the field.
                  setRawTerm(val);
                  editTerms({
                    term: termDisplayToDays(
                      Number(val),
                      values.frequency,
                      offerDefaults.businessDaysPerMonth,
                      offerTermAlwaysDays
                    ),
                  });
                  return;
                }
                if (val === "") {
                  setValues((prev) => ({ ...prev, term: 0 }));
                  return;
                }
                const display = Math.max(1, Number(val) || 1);
                setValues((prev) => ({
                  ...prev,
                  term: termDisplayToDays(
                    display,
                    prev.frequency,
                    offerDefaults.businessDaysPerMonth,
                    offerTermAlwaysDays
                  ),
                }));
              }}
            />
          )}
        </div>
        {checkTerms && !readOnly && <FieldProblem text={problems.term} />}
      </div>

      {/* Its own height, not the row's: a message under Funding or Term makes the row taller. */}
      <div className="relative self-start rounded-md border border-border">
        <span className={floatLabel}>Freq</span>
        {readOnly ? (
          <div className="flex h-8 items-center rounded-md bg-muted/60 px-2 text-[0.7rem] font-semibold capitalize text-muted-foreground/90">
            {values.frequency}
          </div>
        ) : (
          <Select
            value={values.frequency}
            onValueChange={(v) => {
              setRawTerm(null);
              editTerms({ frequency: v });
            }}
          >
            <SelectTrigger size="sm" className="w-full border-0 text-xs shadow-none focus:ring-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily" className="text-xs">
                Daily
              </SelectItem>
              <SelectItem value="weekly" className="text-xs">
                Weekly
              </SelectItem>
              <SelectItem value="monthly" className="text-xs">
                Monthly
              </SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );

  if (inline) {
    return <div className={cn(calculatorContainer, readOnly && "opacity-80")}>{row}</div>;
  }

  return (
    <div
      className={cn(
        calculatorContainer,
        "box-border h-full p-3",
        readOnly && "rounded-md border border-border/60 bg-muted/40 opacity-90"
      )}
    >
      <div className="mb-2 text-[0.7rem] font-medium uppercase tracking-wide text-muted-foreground">
        Offer Builder
      </div>
      {row}
    </div>
  );
};

export default OfferCalculator;
